/** requestLocation() の戻り値。座標が取れたかどうかと座標のみを持つ最小形（#3583） */
export type GeoLocationSnapshot = { lat: number; lon: number; available: boolean }

/** ARフォト／音声ガイド API の body。座標が無いときは空オブジェクト（#3445） */
export type GeoBody = { lat: number; lon: number } | Record<string, never>

export type LocationSource = {
  available: boolean
  lat: number
  lon: number
  /** 省略時（Storybook 等）は再送しない */
  requestLocation?: () => Promise<GeoLocationSnapshot>
}

export type LocationRetryResult<T> = {
  /** 最終的に採用するレスポンス（再送した場合は 2 回目） */
  response: T
  /** 座標が必要だったが測位できなかった（拒否・失敗）。ロック文言を「端末の位置情報設定」にする判断に使う */
  locationDenied: boolean
  /** 測位して再送したか（テスト・ログ用） */
  retried: boolean
}

/**
 * 座標なしで POST し、サーバーが「座標があれば結果が変わる」（location_required）と答えたときだけ
 * 測位して座標つきで再 POST する（#3583）。
 *
 * - 取得済みの座標があれば最初から付けて 1 回で終わる
 * - 1 回目で available / stamp_required / too_far が返れば測位しない（許可プロンプトを出さない）
 * - 測位に失敗した場合は 1 回目のレスポンスをそのまま返し、locationDenied: true を立てる
 */
export async function postWithLocationRetry<T>(
  post: (body: GeoBody) => Promise<T>,
  needsLocation: (response: T) => boolean,
  geo: LocationSource,
): Promise<LocationRetryResult<T>> {
  const first = await post(geo.available ? { lat: geo.lat, lon: geo.lon } : {})
  if (geo.available || !needsLocation(first) || !geo.requestLocation) {
    return { response: first, locationDenied: false, retried: false }
  }

  const location = await geo.requestLocation()
  if (!location.available) {
    return { response: first, locationDenied: true, retried: false }
  }

  const second = await post({ lat: location.lat, lon: location.lon })
  return { response: second, locationDenied: false, retried: true }
}
